import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, ViewChild, output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonService, CommonHttpService } from '../../services';
import { Editor } from 'primeng/editor';
import { CommonMethods, Severity } from '../../helpers/common-methods';
import { ConfirmationService } from 'primeng/api';
import { SpinnerService } from '../../services/spinner.service';
import { RadioButtonModule } from 'primeng/radiobutton';

// Interfaces for better type safety
interface TimeOption {
  name: string;
  code: string;
}

interface DayOption {
  name: string;
  code: number;
}

interface NotifyOption {
  name: string;
  code: string;
}

interface ReportData {
  id?: number;
  savedReportId?: number;
  reportId?: number;
  moduleId?: number;
  reportName?: string;
  subject?: string;
  cronFrequencyType?: number;
  cronFrequencyDate?: number;
  cronFrequencyWeek?: number;
  cronFrequencySelectedTime?: string;
  CronFrequencyHrs?: string | number;
  CronFrequencySlot?: string | number;
  alertTo?: string | string[];
  bodyDesc?: string;
  automationPeriodValue?: number;
  tableNameApplicant?: string;
  tableColumnName?: string;
  checkCronFrequencyHrs?: string | number;
  checkCronFrequencySlot?: string | number;
  checkTimezone?: string;
  checkIsttime?: string;
  mergeField3?: string;
  currentTab?: number;
  isScheduledReport?: boolean;
}

interface ApiResponse {
  status: number;
  data?: any;
  message?: string;
}

@Component({
  selector: 'bi-schedule-report',
  standalone: false,
  templateUrl: './schedule-report.component.html',
  styleUrl: './schedule-report.component.scss',
  providers: [ConfirmationService],
  //imports: [RadioButtonModule]
})
export class ScheduleReportComponent implements OnInit, OnDestroy {
  @ViewChild('editor') editor!: Editor;
  refreshReportsList = output<any>();
  // Component state
  currentTab: number = 0;
  scheduleRepotVisible: boolean = false;
  scheduleForm!: FormGroup;
  isScheduledReport: boolean = false;
  selectedFrequency: string = '1';
  timezoneBlock: boolean = false;
  dayAbbr: any;

  // Loading states
  isComponentLoading: boolean = false;
  isLoadingScheduledReports: boolean = false;
  isLoadingTimeZones: boolean = false;
  isDataReady: boolean = false;

  // Data arrays
  hours: TimeOption[] = [];
  minutes: TimeOption[] = [
    { name: '00', code: '00' },
    { name: '30', code: '30' }
  ];
  weekDays: DayOption[] = [];
  daysOfMonth: TimeOption[] = [];
  timeZones: TimeOption[] = [];
  notifyList: NotifyOption[] = [];
  columns: TimeOption[] = [];

  // Static data
  // 1-based mapping used by backend: 1=Monday ... 7=Sunday
  dayAbbreviations = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  moduleList: TimeOption[] = [
    { name: 'Users', code: 'User' }
  ];

  columnList: TimeOption[] = [
    { name: 'First Name', code: 'first_name' },
    { name: 'Middle Name', code: 'middle_name' },
    { name: 'Display Name', code: 'display_name' },
    { name: 'Last Name', code: 'last_name' },
    { name: 'Email', code: 'email_id' },
    { name: 'Role', code: 'role_id' },
    { name: 'Contact Number', code: 'contact_number' },
  ];

  periodList: TimeOption[] = [];
  private allPeriodList: TimeOption[] = [];

  actionList: TimeOption[] = [
    { name: 'Subject', code: 'subject' },
    { name: 'Body', code: 'body' },
  ];

  // Component data
  reportItem: ReportData = {};
  savedReports: ReportData[] = [];
  
  // Properties for text truncation
  expandedSubjects: { [key: number]: boolean } = {};
  expandedRecipients: { [key: number]: boolean } = {};
  expandedEmailBody: { [key: number]: boolean } = {};

  // Subscription management
  private _subscription: Subscription = new Subscription();

  constructor(
    private _commonService: CommonService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonHttpService: CommonHttpService,
    private _fb: FormBuilder,
    private _commonMethods: CommonMethods,
    private confirmationService: ConfirmationService,
    private spinnerService: SpinnerService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Ensure component starts in hidden state
    this.scheduleRepotVisible = false;
    this.isComponentLoading = false;
    this.isLoadingScheduledReports = false;
    this.isLoadingTimeZones = false;
    this.isDataReady = false;
    
    // Set default frequency to first radio button (Daily)
    this.selectedFrequency = '1';

    this.initializations();
    this.setupScheduleReportSubscription();
  }

  initForm(): void {
    this.scheduleForm = this._fb.group({
      id: [null],
      savedReportId: [null],
      cronFrequencyType: ['1'], // Default to '1' for Daily (first radio button)
      cronFrequencyDate: [null],
      cronFrequencyWeek: [null],
      cronFrequencySelectedTime: [null],
      CronFrequencyHrs: ['',Validators.required],
      CronFrequencySlot: ['',Validators.required],
      alertTo: ['', Validators.required],
      subject: [''],
      bodyDesc: ['', Validators.required],
      automationPeriodValue: ['', Validators.required],
      tableNameApplicant: [null],
      tableColumnName: [null],
      checkCronFrequencyHrs: [null],
      checkCronFrequencySlot: [null],
      checkTimezone: [null],
      checkIsttime: [null],
      mergeField3: [null],
    });

    // Watch for frequency changes to update form validation
    this.scheduleForm.get('cronFrequencyType')?.valueChanges.subscribe(frequency => {
      if (frequency) {
        this.selectedFrequency = frequency.toString();
        this.updateFormValidation();
      }
    });
  }

  updateFormValidation(): void {
    const weeklyDayControl = this.scheduleForm.get('cronFrequencyWeek');
    const monthlyDateControl = this.scheduleForm.get('cronFrequencyDate');

    if (this.selectedFrequency === '2') {
      weeklyDayControl?.setValidators([Validators.required]);
      monthlyDateControl?.clearValidators();
    } else if (this.selectedFrequency === '3') {
      monthlyDateControl?.setValidators([Validators.required]);
      weeklyDayControl?.clearValidators();
    } else {
      weeklyDayControl?.clearValidators();
      monthlyDateControl?.clearValidators();
    }

    weeklyDayControl?.updateValueAndValidity();
    monthlyDateControl?.updateValueAndValidity();
  }

  selectFrequency(frequency: string): void {
    this.selectedFrequency = frequency;
    this.scheduleForm.get('cronFrequencyType')?.setValue(Number(frequency));
       
    this.updateFormValidation();
  }

  onSubmit(): void {
    if (this.scheduleForm.valid) {
      const formData = this.scheduleForm.value;
      const transformedData = this.transformFormData(formData);
      console.log('transformedData',transformedData);
      this.spinnerService.withSpinner(
        this._commonHttpService.saveReportAutomation(transformedData),
        'Saving schedule...'
      ).subscribe({
        next: (data: any) => {
          // console.log('API Response:', data);
          
          // Check if response has status property
          if (data && typeof data === 'object' && 'status' in data) {
            if (data.status === 1) {
              
              this._commonMethods.showToastMessage(data.message, 'Success', Severity.success as Severity);
              
                this.refreshReportsList.emit(this.currentTab);
                this.triggerReportDetailsRefresh();
                this.onCancel();
              
            } else {
              this._commonMethods.showToastMessage(data.message || 'Failed to save schedule', 'Error', Severity.error as Severity);
            }
          } else {
            // If no status property, assume success if we get a response
            this._commonMethods.showToastMessage(data.message, 'Success', Severity.success as Severity);
            this.refreshReportsList.emit(this.currentTab);
              this.refreshReportsList.emit(this.currentTab);
              this.onCancel();
            
            this.triggerReportDetailsRefresh();
            this.onCancel();

          }
        },
        error: (error) => {
          console.error('Error saving schedule:', error);
          this._commonMethods.showToastMessage('Failed to save schedule', 'Error', Severity.error as Severity);
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private transformFormData(formData: any): any {
    const transformed = { ...formData };

    // Fields that should be converted from {name, code} to just code
    const fieldsToTransform = [
      'CronFrequencyHrs',
      'CronFrequencySlot',
      'cronFrequencyWeek',
      'cronFrequencyDate',
      'automationPeriodValue',
      'tableNameApplicant',
      'tableColumnName',
      'checkCronFrequencyHrs',
      'checkCronFrequencySlot',
      'checkTimezone',
      'mergeField3'
    ];

    fieldsToTransform.forEach(field => {
      if (transformed[field] && typeof transformed[field] === 'object' && transformed[field].code !== undefined) {
        transformed[field] = transformed[field].code;
      }
    });

    // Fields that should be converted to numbers
    const numericFields = [
      'id',
      'savedReportId',
      'cronFrequencyType',
      'cronFrequencyDate',
      'cronFrequencyWeek',
      'cronFrequencySelectedTime',
      'CronFrequencyHrs',
      'checkCronFrequencyHrs',
      'CronFrequencySlot',
      'checkCronFrequencySlot'
    ];

    numericFields.forEach(field => {
      if (transformed[field] !== null && transformed[field] !== undefined && transformed[field] !== '') {
        let value = transformed[field];
        if (typeof value === 'object' && value.code !== undefined) {
          value = value.code;
        }
        transformed[field] = Number(value);
      }
    });

    // Handle alertTo array - extract codes from each object
    if (transformed.alertTo && Array.isArray(transformed.alertTo)) {
      transformed.alertTo = transformed.alertTo.map((item: any) => {
        if (item && typeof item === 'object' && item.code !== undefined) {
          return item.code;
        }
        return item;
      });
    }

    return transformed;
  }

  markFormGroupTouched(): void {
    Object.keys(this.scheduleForm.controls).forEach(key => {
      const control = this.scheduleForm.get(key);
      control?.markAsTouched();
    });
  }

  onCancel(): void {
    
    this._commonService.hideScheduleReport();
    this.scheduleRepotVisible = false;
    this.isComponentLoading = false;
    this.isLoadingScheduledReports = false;
    this.isLoadingTimeZones = false;
    this.isDataReady = false;
    this.isScheduledReport = false;
    this.scheduleForm.reset();
    this.timezoneBlock = false;
    this.savedReports = [];

  }

  /**
   * Triggers refresh of report details component after successful schedule save
   */
  private triggerReportDetailsRefresh(): void {
    // Set the reload data with current report information
    this._commonService.setReloadSavedReportData = {
      moduleId: this.reportItem.id,
      reportId: this.reportItem.savedReportId || this.reportItem.id,
      reloadSavedReportData: true
    };
    
    // Trigger the refresh
    this._commonService.triggerGetSavedReportData();
  }

  initializations(): void {
    // Ensure dayAbbreviations is properly initialized (1=Monday ... 7=Sunday)
    if (!this.dayAbbreviations || this.dayAbbreviations.length === 0) {
      this.dayAbbreviations = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    }
    
    // Initialize week days with 1-based codes matching backend cronFrequencyWeek
    this.weekDays = Array.from({ length: 7 }, (_, i) => {
      const dayAbbr = this.dayAbbreviations[i];
      return { name: dayAbbr.toUpperCase(), code: i + 1 };
    });

    // Initialize hours
    this.hours = Array.from({ length: 24 }, (_, i) => {
      const formattedHour = i.toString().padStart(2, '0');
      return { name: formattedHour, code: formattedHour };
    });

    // Initialize days of month
    this.daysOfMonth = Array.from({ length: 30 }, (_, i) => {
      const dayNumber = (i + 1).toString();
      return { code: dayNumber, name: dayNumber };
    });

    // Add the "End of month" entry
    this.daysOfMonth.push({ code: "31", name: "End of the month" });
  }

  getTimeZones(): void {
    this.isLoadingTimeZones = true;

    this.spinnerService.withSpinner(
      this._commonHttpService.getTimeZones(),
      'Loading timezones...'
    ).subscribe({
      next: (data: any) => {
        try {
          let timezoneData: any[] = [];
          let userList: any[] = [];
          let teamList: any[] = [];
          let roleList: any[] = [];
          let periodList: any[] = [];
          if (data?.periodList) {
            periodList = data.periodList;
          } else if (data?.data?.periodList) {
            periodList = data.data.periodList;
          }
          this.applyPeriodList(periodList);

          // Extract timezone data
          if (data?.defaultTimeList) {
            timezoneData = data.defaultTimeList;
          } else if (data?.data?.defaultTimeList) {
            timezoneData = data.data.defaultTimeList;
          } else if (data?.data?.timezones) {
            timezoneData = data.data.timezones;
          } else if (data?.timezones) {
            timezoneData = data.timezones;
          } else if (data?.data) {
            timezoneData = data.data;
          }

          // Extract role data
          if (data?.roles) {
            roleList = data.roles;
          } else if (data?.data?.roles) {
            roleList = data.data.roles;
          }

          // Extract user data
          if (data?.userList) {
            userList = data.userList;
          } else if (data?.data?.userList) {
            userList = data.data.userList;
          }

          // Extract team data
          if (data?.teams) {
            teamList = data.teams;
          } else if (data?.data?.teams) {
            teamList = data.data.teams;
          }

          this.notifyList = [...roleList, ...teamList, ...userList];

          if (timezoneData && Array.isArray(timezoneData)) {
            this.timeZones = timezoneData;
          } else {
            this.timeZones = [];
          }
        } catch (error) {
          console.error('Error processing timezone data:', error);
          this.timeZones = [];
          this.notifyList = [];
        }

        this.isLoadingTimeZones = false;
        this.checkIfDataReady();
      },
      error: (error) => {
        console.error('Error fetching timezones:', error);
        this.timeZones = [];
        this.notifyList = [];
        this.isLoadingTimeZones = false;
        this.checkIfDataReady();
      }
    });
  }

  setupScheduleReportSubscription(): void {
    this._subscription.add(
      this._commonService.scheduleReportState.subscribe({
        next: (state: any) => {
          if (!state || typeof state.show !== 'boolean') {
            this.scheduleRepotVisible = false;
            return;
          }

          this.scheduleRepotVisible = state.show;

          if (this.scheduleRepotVisible && state.show) {
            const reportData = this._commonService.scheduleReportData;
            if (!reportData || !reportData.id) {
              this.scheduleRepotVisible = false;
              return;
            }

            this.isComponentLoading = true;
            this.isLoadingScheduledReports = false;
            this.isLoadingTimeZones = false;
            this.isDataReady = false;

            setTimeout(() => {
              this.reportItem = this._commonService.scheduleReportData;
              this.currentTab = this.reportItem.currentTab || 0;
              this.applyPeriodList();

              if (this.reportItem.isScheduledReport) {
                this.isScheduledReport = true;
                this.getScheduledReports();
                this._changeDetectorRef.detectChanges();
              } else {
                this.getTimeZones();
                this.isScheduledReport = false;
                this.isLoadingScheduledReports = false;
                this.checkIfDataReady();
                this._changeDetectorRef.detectChanges();
                this.scheduleForm.patchValue({
                  id: null,
                  subject: this.reportItem.reportName,
                  savedReportId: this.reportItem.id,
                  cronFrequencyType: '1' // Select first radio button (Daily) by default
                });
                this.selectedFrequency = '1'; // Set the selected frequency
                this.updateFormValidation();
              }
            }, 100);
          } else {
            this.isComponentLoading = false;
            this.isLoadingScheduledReports = false;
            this.isLoadingTimeZones = false;
            this.isDataReady = false;
            this.scheduleRepotVisible = false;
          }
        },
        error: (error) => {
          console.error('Subscription error:', error);
        }
      })
    );
  }
 timeZoneArrowClass = 'pi pi-angle-down';
  openTimezoneModal(): void {
    // Toggle the timezone block - if it's already open, close it; if closed, open it
    this.timezoneBlock = !this.timezoneBlock;
    this.timeZoneArrowClass = this.timezoneBlock ? 'pi pi-angle-up' : 'pi pi-angle-down';
    
    // If opening the timezone block, clear the timezone-related form inputs
    if (this.timezoneBlock) {
      this.clearTimezoneFormInputs();
    }
  }
  
  closeTimezoneModal(): void {
    this.timezoneBlock = false;
  }

  /**
   * Clears all timezone-related form inputs
   */
  private clearTimezoneFormInputs(): void {
    this.scheduleForm.patchValue({
      checkCronFrequencyHrs: null,
      checkCronFrequencySlot: null,
      checkTimezone: null,
      checkIsttime: null
    });
  }
  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  onModuleChange(event: any): void {
    if (event?.value && event.value === 'User') {
      this.columns = [...this.columnList];
    } else {
      this.columns = [];
    }
  }

  addMergeField(): void {
    const mergeField = this.scheduleForm.get('tableColumnName')?.value;
    const actionType = this.scheduleForm.get('mergeField3')?.value;
    if (!mergeField) {
      this._commonMethods.showToastMessage('Please select a column first', 'Warning', Severity.warn as Severity);
      return;
    } else if (!actionType) {
      this._commonMethods.showToastMessage('Please select Subject or Body', 'Warning', Severity.warn as Severity);
      return;
    }

    const fieldData = `{User:${mergeField}}`;

    if (actionType === "body") {
      const editorInstance = this.getEditorInstance();
      if (editorInstance) {
        this.insertTextAtCursor(fieldData);
        console.log('Text inserted into editor');
      } else {
        console.log('No editor instance found, using fallback');
        // Fallback: append to current body content
        const currentBody = this.scheduleForm.get('bodyDesc')?.value || '';
        this.scheduleForm.patchValue({ bodyDesc: currentBody + fieldData });
      }
    } else {
      const currentSubject = this.scheduleForm.get('subject')?.value || '';
      this.scheduleForm.patchValue({ 'subject': currentSubject + fieldData });
    }
  }

  getEditorInstance(): Editor | null {
    return this.editor || null;
  }

  setEditorContent(content: string): void {
    if (this.editor) {
      this.editor.value = content;
      this.scheduleForm.patchValue({ bodyDesc: content });
    }
  }

  getEditorContent(): string {
    return this.editor?.value || '';
  }

  insertTextAtCursor(text: string): void {
    if (this.editor?.getQuill) {
      const quill = this.editor.getQuill();
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          quill.insertText(range.index, text);
        } else {
          // If no selection, append to the end
          const length = quill.getLength();
          quill.insertText(length - 1, text);
        }
        // Update the form control with the new content
        this.scheduleForm.patchValue({ bodyDesc: quill.root.innerHTML });
      }
    } else {
      // Fallback: append to current editor value
      const currentContent = this.scheduleForm.get('bodyDesc')?.value || '';
      this.scheduleForm.patchValue({ bodyDesc: currentContent + text });
    }
  }

  clearEditor(): void {
    if (this.editor) {
      this.editor.value = '';
      this.scheduleForm.patchValue({ bodyDesc: '' });
    }
  }

  getScheduledReports(): void {
    const savedReportId = this.reportItem.currentTab === 2 ?
      this.reportItem.savedReportId : this.reportItem.id;

    if (!savedReportId) {
      console.error('No saved report ID available');
      this.savedReports = [];
      this.isLoadingScheduledReports = false;
      this.checkIfDataReady();
      return;
    }

    this.isLoadingScheduledReports = true;

    this.spinnerService.withSpinner(
      this._commonHttpService.getScheduledSavedReports(savedReportId),
      'Loading scheduled reports...'
    ).subscribe({
      next: (data: ApiResponse) => {
        if (data.status === 1 && data.data?.modules?.[0]?.savedReports) {
          this.savedReports = data.data.modules[0].savedReports;
          this.refreshReportsList.emit(this.currentTab);
          this.savedReports.forEach(element => {
            if (element.cronFrequencySelectedTime) {
              const time = element.cronFrequencySelectedTime.split(':');

              if (time.length > 0) {
                element.CronFrequencyHrs = time[0];
                element.CronFrequencySlot = time[1];
              }
            }
          });
        } else {
          this.savedReports = [];
        }
        this.isLoadingScheduledReports = false;
        this.getTimeZones();
        this.checkIfDataReady();
      },
      error: (error) => {
        console.error('Error fetching scheduled reports:', error);
        this.savedReports = [];
        this.isLoadingScheduledReports = false;
        this.checkIfDataReady();
      }
    });
  }

  checkIfDataReady(): void {
    if (!this.isLoadingScheduledReports && !this.isLoadingTimeZones) {
      this.isDataReady = true;
      this.isComponentLoading = false;
    }
  }

  createNewSchedule(): void {
    this.isScheduledReport = false;
    this.scheduleForm.reset();
    this.selectedFrequency = '1'; // Select first radio button (Daily) by default
    this.scheduleForm.patchValue({
      id: null,
      subject: this.reportItem.reportName,
      savedReportId: this.reportItem.savedReportId,
      cronFrequencyType: '1' // Select first radio button (Daily) by default
    });
    this.updateFormValidation();
  }

  getFrequencyText(frequencyType: number | string | undefined): string {
    if (frequencyType === undefined || frequencyType === null) {
      return 'Unknown';
    }
    const type = frequencyType.toString();
    switch (type) {
      case '1':
        return 'Daily';
      case '2':
        return 'Weekly';
      case '3':
        return 'Monthly';
      default:
        return 'Unknown';
    }
  }

  /**
   * Maps saved weekly frequency day to display name.
   * Backend / form values are 1-based: 1=Monday, 2=Tuesday, ... 7=Sunday.
   */
  getWeekDayName(weekDay: number | string | null | undefined): string {
    const dayNumber = Number(weekDay);
    if (!dayNumber || dayNumber < 1 || dayNumber > 7) {
      return '';
    }
    return this.dayAbbreviations[dayNumber - 1] || '';
  }

  // Method to format day of month with ordinal suffix
  getDayOfMonth(day: number): string {
    if (day === undefined || day === null) {
      return 'Unknown day';
    }

    const suffix = this.getDaySuffix(day);
    return `${day}${suffix}`;
  }

  // Method to get day suffix (1st, 2nd, 3rd, etc.)
  getDaySuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  editSchedule(report: ReportData): void {
    try {
      // Handle time parsing
      if (report.cronFrequencySelectedTime) {
        const time = report.cronFrequencySelectedTime.split(':');

        if (time.length > 0) {
          report.CronFrequencyHrs = time[0];
          report.CronFrequencySlot = time[1];
        }
      }

      // Format hours with leading zero if needed
      if (report.CronFrequencyHrs && Number(report.CronFrequencyHrs) < 10) {
        report.CronFrequencyHrs = report.CronFrequencyHrs;
      }

      this.initForm();
      this.initializations();

      setTimeout(() => {
        this.isScheduledReport = false;
        this.scheduleForm.reset();

        // Safely patch form values with null checks
        this.scheduleForm.patchValue({
          id: report.id || null,
          subject: report.subject || report.reportName || '',
          savedReportId: report.savedReportId || null,
          cronFrequencyType: report.cronFrequencyType || null,
          cronFrequencyDate: report.cronFrequencyDate?.toString() || null,
          cronFrequencyWeek: report.cronFrequencyWeek != null
            ? Number(report.cronFrequencyWeek)
            : null,
          CronFrequencyHrs: report.CronFrequencyHrs || null,
          CronFrequencySlot: report.CronFrequencySlot?.toString() || null,
          alertTo: report.alertTo ? (typeof report.alertTo === 'string' ? report.alertTo.split(',') : report.alertTo) : [],
          bodyDesc: report.bodyDesc || '',
          automationPeriodValue: report.automationPeriodValue?.toString() || null,
          checkCronFrequencyHrs: report.checkCronFrequencyHrs || null,
          checkCronFrequencySlot: report.checkCronFrequencySlot || null,
          checkTimezone: report.checkTimezone || null,
          checkIsttime: report.checkIsttime || null,
        });

        this.selectedFrequency = report.cronFrequencyType?.toString() || '1';
        this.updateFormValidation();
        this.clearInvalidAutomationPeriodValue();
      }, 100);
    } catch (error) {
      console.error('Error in editSchedule:', error);
      this._commonMethods.showToastMessage('Error loading schedule data', 'Error', Severity.error as Severity);
    }
  }

  deleteSchedule(report: ReportData): void {
    const frequencyText = this.getFrequencyText(report.cronFrequencyType || 1);
    const reportName = report.reportName || report.subject || 'Untitled Report';
    const timeInfo = report.CronFrequencyHrs && report.CronFrequencySlot
      ? ` at ${report.CronFrequencyHrs}:${report.CronFrequencySlot}`
      : '';

    const confirmMessage = `Are you sure you want to delete the schedule "${reportName}"?`;

    this.confirmationService.confirm({
      message: confirmMessage,
      header: 'Delete Schedule Confirmation',
      //icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger p-component',
      rejectButtonStyleClass: 'p-button-secondary p-component p-button-outlined',
      accept: () => {
        this.performDelete(report, reportName);
        this.onCancel();
      },
      reject: () => {
        // User cancelled the deletion
        console.log('Schedule deletion cancelled by user');
      }
    });
  }

  private performDelete(report: ReportData, reportName: string): void {
    console.log('Deleting schedule for report:', report);
    
    // Validate report ID before proceeding
    if (!report.id) {
      this._commonMethods.showToastMessage('Invalid report ID. Cannot delete schedule.', 'Error', Severity.error as Severity);
      return;
    }

    // Show loading spinner and delete the schedule
    this.spinnerService.withSpinner(
      this._commonHttpService.deleteScheduledReport(report.id),
      'Deleting schedule...'
    ).subscribe({
      next: (response: ApiResponse) => {
        if (response.status === 1) {
          this._commonMethods.showToastMessage(
            `Schedule for "${reportName}" deleted successfully`, 
            'Success', 
            Severity.success as Severity
          );
          // Refresh the scheduled reports list
          this.getScheduledReports();
          this.refreshReportsList.emit(this.currentTab);
        } else {
          this._commonMethods.showToastMessage(
            response.message || 'Failed to delete schedule. Please try again.', 
            'Error', 
            Severity.error as Severity
          );
        }
      },
      error: (error) => {
        console.error('Error deleting schedule:', error);
        this._commonMethods.showToastMessage(
          'An error occurred while deleting the schedule. Please try again.', 
          'Error', 
          Severity.error as Severity
        );
      }
    });
  }


  addNewSchedule(): void {
    this.initForm();
    this.initializations();
    if(this.savedReports.length >= 3){
      this._commonMethods.showToastMessage('You can schedule up to 3 reports', 'Error', Severity.error as Severity);
      return;
    }
    this.isScheduledReport = false;
    this.scheduleForm.reset();
    this.getTimeZones();
    this.isComponentLoading = false;
    this.isLoadingTimeZones = false;
    this.isDataReady = false;
    this.isLoadingScheduledReports = false;
    this.checkIfDataReady();
    // Select first radio button (Daily) by default when no data exists
    this.selectedFrequency = '1'; // Select first radio button (Daily) by default
    this.scheduleForm.patchValue({
      id: null,
      subject: this.reportItem.reportName,
      savedReportId: this.reportItem.id,
      cronFrequencyType: '1' // Select first radio button (Daily) by default
    });
    this.updateFormValidation();
    this._changeDetectorRef.detectChanges();
  }

  getRecipientNames(alertTo: any | any[] | undefined): any {
    if (!alertTo) return 'No recipients';
    let recipientNames: any[] = [];
    let recipientIds = Array.isArray(alertTo) ? alertTo : alertTo.split(',');
    // Map recipient IDs to names using notifyList
    recipientNames = recipientIds.map((id: any) => {

      // Try multiple matching strategies
      let recipient = this.notifyList.find(n => n.code === id);

      if (!recipient) {
        // Try string comparison
        recipient = this.notifyList.find(n => n.code === id.toString());
      }
      if (!recipient) {
        // Try number comparison if id is a string that can be converted to number
        const numId = Number(id);
        if (!isNaN(numId)) {
          recipient = this.notifyList.find(n => String(n.code) === String(numId));
        }
      }

      if (!recipient) {
        // Try case-insensitive comparison
        recipient = this.notifyList.find(n =>
          n.code.toString().toLowerCase() === id.toString().toLowerCase()
        );
      }
      return recipient ? recipient.name : `Unknown (${id})`;
    }).filter((name: any) => name && name.length > 0);

    return recipientNames.length > 0 ? recipientNames.join(', ') : 'No recipients';
  }

  // Keep the old method for backward compatibility
  getRecipientsCount(alertTo: any | any[] | undefined): any {
    return this.getRecipientNames(alertTo);
  }

  getConvertedTimezone(): void {
    if (this.scheduleForm.get('checkTimezone')?.value && this.scheduleForm.get('checkCronFrequencyHrs')?.value && this.scheduleForm.get('checkCronFrequencySlot')?.value) {
      this.spinnerService.withSpinner(
        this._commonHttpService.getConvertedTimezone(this.scheduleForm.value),
      ).subscribe({
        next: (data: any) => {
          console.log('data', data);
          console.log('data.data.estTime', data.estTime);
        
          this.scheduleForm.patchValue({
            checkIsttime: data.estTime,  
          });
        }
      });
    }
  }

  /**
   * Truncates text to 100 characters and adds ellipsis if needed
   */
  truncateText(text: string | undefined, maxLength: number = 100): string {
    if (!text || text.length <= maxLength) {
      return text || '';
    }
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Checks if text should be truncated
   */
  shouldTruncate(text: string | undefined, maxLength: number = 100): boolean {
    return !!(text && text.length > maxLength);
  }


  
  

  /**
   * Toggles the expanded state for a specific report's subject
   */
  toggleSubject(reportId: number): void {
    this.expandedSubjects[reportId] = !this.expandedSubjects[reportId];
  }


  /**
   * Toggles the expanded state for a specific report's recipients
   */
  toggleRecipients(reportId: number): void {
    this.expandedRecipients[reportId] = !this.expandedRecipients[reportId];
  }

  /**
   * Checks if a report's recipients are expanded
   */
  isRecipientsExpanded(reportId: number): boolean {
    return this.expandedRecipients[reportId] || false;
  }

  /**
   * Strips HTML tags and entities from text
   */
  stripHtmlTags(html: string | undefined): string {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular space
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .replace(/&quot;/g, '"') // Replace &quot; with "
      .replace(/&#39;/g, "'") // Replace &#39; with '
      .trim(); // Remove leading/trailing whitespace
  }

  /**
   * Toggles the expanded state for a specific report's email body
   */
  toggleEmailBody(reportId: number): void {
    this.expandedEmailBody[reportId] = !this.expandedEmailBody[reportId];
  }

  /**
   * Checks if a report's email body is expanded
   */
  isEmailBodyExpanded(reportId: number): boolean {
    return this.expandedEmailBody[reportId] || false;
  }

  private getScheduleReportId(): number {
    return Number(this.reportItem?.reportId ?? 0);
  }

  private filterPeriodListByModule(periodList: TimeOption[]): TimeOption[] {
    if (this.getScheduleReportId() === 1 || this.getScheduleReportId() === 6) {
      return periodList;
    }

    return periodList.filter(period => String(period.code) !== '18');
  }

  private applyPeriodList(periodList?: TimeOption[]): void {
    if (periodList) {
      this.allPeriodList = periodList;
    }

    this.periodList = this.filterPeriodListByModule(this.allPeriodList);
    this.clearInvalidAutomationPeriodValue();
  }

  private clearInvalidAutomationPeriodValue(): void {
    const currentValue = this.scheduleForm?.get('automationPeriodValue')?.value;
    if (currentValue == null || currentValue === '') {
      return;
    }

    const currentCode = typeof currentValue === 'object' ? currentValue.code : currentValue;
    if (String(currentCode) === '18' && this.getScheduleReportId() !== 1 && this.getScheduleReportId() !== 6) {
      this.scheduleForm.patchValue({ getScheduleReportId: null });
    }
  }
}

